import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { userSelectors } from 'store/features/user/selectors'

import { useCallback } from 'react'

import { IAIVideoModel } from 'types/aiVideo'
import { saveAIVideoUserPreferences } from 'utils/localStorage'
import { MIN_PREMIUM_QUALITY, PREMIUM_AI_MODELS_UPGRADE } from 'consts'
import { REF_LINKS } from 'consts/refLinks'

interface IQualityOption {
  quality: number
  credit: number
  generationAvgDurationSeconds: number
}

const findQualityOption = (
  model: IAIVideoModel | null,
  duration: number,
  quality: number
): IQualityOption | null => {
  if (!model) {
    return null
  }

  const durationOption = model.options.find(opt => opt.duration === duration)
  if (!durationOption) {
    return null
  }

  return durationOption.qualities.find(q => q.quality === quality) || null
}

export const useActions = () => {
  const aiVideoModels = useSelector(aiVideoSelectors.aiVideoModels)
  const selectedModel = useSelector(aiVideoSelectors.selectedModel)
  const duration = useSelector(aiVideoSelectors.selectedDurationOption)
  const quality = useSelector(aiVideoSelectors.quality)
  const isFreeUser = useSelector(userSelectors.isFreeUser)

  const handleModelChange = useCallback(
    (modelCode: string) => {
      const model = aiVideoModels.find(m => m?.model === modelCode)
      if (model) {
        dispatch(aiVideoThunks.updateSelectedModel({ modelName: model.model }))
        dispatch(aiVideoThunks.saveCurrentSettingsAsDefault())
        if (!model.capabilities.firstAndLastFrameSupport.isSupported) {
          dispatch(aiVideoThunks.removeLastFrame())
        }
      }
    },
    [aiVideoModels]
  )

  const handleDurationChange = useCallback(
    (value: string | number) => {
      const durationValue = Number(value)
      dispatch(aiVideoThunks.setDuration(durationValue))

      const currentQualityOption = findQualityOption(
        selectedModel,
        durationValue,
        quality
      )

      //todoA: check why we check currentQualityOption, check if we can check model existnece before opening the model ui part
      if (currentQualityOption) {
        dispatch(
          aiVideoThunks.setVideoGenerationCost(currentQualityOption.credit)
        )
        dispatch(aiVideoThunks.saveCurrentSettingsAsDefault())
        return
      }

      const fallbackQualityOption = findQualityOption(
        selectedModel,
        durationValue,
        selectedModel.defaultValues.quality
      )

      if (fallbackQualityOption) {
        dispatch(
          aiVideoThunks.setVideoGenerationCost(fallbackQualityOption.credit)
        )
        dispatch(aiVideoThunks.setQuality(fallbackQualityOption.quality))
      }

      dispatch(aiVideoThunks.saveCurrentSettingsAsDefault())
    },
    [quality, selectedModel]
  )

  const handleAspectRatioChange = useCallback((value: string | number) => {
    dispatch(aiVideoThunks.setAspectRatio(String(value)))
    dispatch(aiVideoThunks.saveCurrentSettingsAsDefault())
  }, [])

  const handleImageAspectRatioChange = useCallback((value: string | null) => {
    dispatch(aiVideoThunks.setAspectRatio(value))
    dispatch(aiVideoThunks.saveCurrentSettingsAsDefault())
  }, [])

  const handleQualityChange = useCallback(
    (value: string | number) => {
      const qualityValue = Number(value)

      if (isFreeUser && qualityValue >= MIN_PREMIUM_QUALITY) {
        window.openUpgradePopup({
          type: PREMIUM_AI_MODELS_UPGRADE,
          openerRef: REF_LINKS.PREMIUM_AI_MODELS
        })
        return
      }

      dispatch(aiVideoThunks.setQuality(qualityValue))
      saveAIVideoUserPreferences({ quality: qualityValue })
      dispatch(aiVideoThunks.saveCurrentSettingsAsDefault())

      const currentQualityOption = findQualityOption(
        selectedModel,
        Number(duration.value),
        qualityValue
      )

      if (currentQualityOption) {
        dispatch(
          aiVideoThunks.setVideoGenerationCost(currentQualityOption.credit)
        )
      }
    },
    [selectedModel, duration, isFreeUser]
  )

  return {
    handleModelChange,
    handleDurationChange,
    handleAspectRatioChange,
    handleImageAspectRatioChange,
    handleQualityChange
  }
}
